import React, { useEffect, useState } from 'react';
import Modal from '../general/Modal';
import { novaThemes, NovaThemeName } from './novaTheme';
import { NovaCloseIcon, NovaDownloadIcon } from './novaIcons';

type NovaExportFormat = 'word' | 'pdf';

const FORMATS: { value: NovaExportFormat; label: string }[] = [
  { value: 'word', label: 'Word' },
  { value: 'pdf', label: 'PDF' },
];

interface NovaDownloadDialogProps {
  open: boolean;
  theme: NovaThemeName;
  defaultName: string;
  exporting?: boolean;
  onClose: () => void;
  onExport: (name: string, format: NovaExportFormat) => Promise<void> | void;
}

// The buildout's "Download → customise export" spec: a name field plus a
// Word/PDF format picker, the selected card taking the gold ring.
const NovaDownloadDialog: React.FC<NovaDownloadDialogProps> = ({
  open,
  theme,
  defaultName,
  exporting = false,
  onClose,
  onExport,
}) => {
  const surfaces = novaThemes[theme];
  const [name, setName] = useState(defaultName);
  const [format, setFormat] = useState<NovaExportFormat>('word');

  useEffect(() => {
    if (open) setName(defaultName);
  }, [open, defaultName]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      zIndex={1000000}
      contanerClassName="w-[420px] max-w-[92vw] !rounded-[20px] p-0"
    >
      <div
        className="rounded-[20px] border p-[26px] pb-6"
        style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.body }}
      >
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className="mb-1 text-[22px] font-semibold tracking-[-0.4px]" style={{ color: surfaces.ink.heading }}>
              Customise export
            </div>
            <div className="text-sm" style={{ color: surfaces.ink.secondary }}>
              Choose a format and what to include.
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-[30px] w-[30px] flex-none cursor-pointer items-center justify-center rounded-[9px] transition-opacity hover:opacity-70"
            style={{ color: surfaces.ink.secondary }}
          >
            <NovaCloseIcon className="h-[17px] w-[17px]" />
          </button>
        </div>

        <div className="mb-2 text-[11px] tracking-[0.12em] uppercase" style={{ color: surfaces.ink.secondary }}>
          Name
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-5 w-full rounded-[11px] border px-3.5 py-2.5 text-[15px] outline-none"
          style={{ background: surfaces.surfaces.field, borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.heading }}
        />

        <div className="mb-2 text-[11px] tracking-[0.12em] uppercase" style={{ color: surfaces.ink.secondary }}>
          Format
        </div>
        <div className="mb-[22px] flex gap-2.5">
          {FORMATS.map(({ value, label }) => {
            const selected = format === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setFormat(value)}
                className="relative flex flex-1 cursor-pointer flex-col items-center gap-1 rounded-xl border p-3.5"
                style={{
                  background: surfaces.surfaces.card,
                  borderColor: selected ? surfaces.actionAccent : surfaces.surfaces.panelBorder,
                  borderWidth: selected ? 1.5 : 1,
                }}
              >
                {selected && (
                  <span
                    className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full"
                    style={{ background: surfaces.actionAccent }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={surfaces.ink.onGold} strokeWidth={3.4} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                )}
                <span style={{ color: selected ? surfaces.ink.heading : surfaces.ink.secondary }}>
                  <NovaDownloadIcon className="h-6 w-6" />
                </span>
                <span
                  className="text-[13.5px]"
                  style={{ fontWeight: selected ? 600 : 500, color: selected ? surfaces.ink.heading : surfaces.ink.secondary }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          disabled={exporting}
          aria-busy={exporting}
          onClick={async () => {
            await onExport(name, format);
            onClose();
          }}
          className="flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-full py-[13px] text-[15px] font-semibold disabled:cursor-wait disabled:opacity-80"
          style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
        >
          {exporting ? (
            <>
              <span
                className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-transparent motion-reduce:animate-none"
                style={{ borderTopColor: surfaces.ink.onGold, borderRightColor: surfaces.ink.onGold }}
              />
              Preparing document…
            </>
          ) : (
            <>
              Export
              <NovaDownloadIcon className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default NovaDownloadDialog;
